import { readProtoCoord } from '@/lib/elements/decode-proto-path-coords'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { type AnyRecord, asArray, asRecord, finiteNumber } from './tolerant-read'

/**
 * Returns true when the pen-path commands form a closed loop.
 * `buildPathFromPoints` always appends an explicit lineTo/curveTo back to the
 * first point (p0) when isClosed=true, so the closing segment is encoded in the
 * path commands themselves and survives the protobuf round-trip.
 * The Z command is NOT a PathCommand, so we detect closure by comparing the
 * last command's endpoint to the first moveTo point.
 */
export function isPenPathCommandsClosed(commands: PathCommand[]): boolean {
  if (commands.length < 2) return false

  const first = commands[0]

  if (!('moveTo' in first)) return false

  const firstX = first.moveTo.x
  const firstY = first.moveTo.y
  const last = commands[commands.length - 1]
  let lastX: number | undefined
  let lastY: number | undefined

  if ('lineTo' in last) {
    lastX = last.lineTo.x
    lastY = last.lineTo.y
  } else if ('curveTo' in last) {
    lastX = last.curveTo.x
    lastY = last.curveTo.y
  }

  if (lastX === undefined || lastY === undefined) return false

  return Math.abs(lastX - firstX) < 0.01 && Math.abs(lastY - firstY) < 0.01
}

export function hasCurveToFields(cmd: AnyRecord): boolean {
  const hasEnd = cmd.x != null && cmd.y != null

  if (!hasEnd) return false

  const hasFirstControl =
    cmd.controlX != null ||
    cmd.control_x != null ||
    cmd.ControlX != null ||
    cmd.controlY != null ||
    cmd.control_y != null ||
    cmd.ControlY != null
  const hasSecondControl =
    cmd.deltaX != null ||
    cmd.deltaY != null ||
    cmd.delta_x != null ||
    cmd.delta_y != null ||
    cmd.DeltaX != null ||
    cmd.DeltaY != null ||
    cmd.controlX2 != null ||
    cmd.controlY2 != null ||
    cmd.control_x2 != null ||
    cmd.control_y2 != null ||
    cmd.ControlX2 != null ||
    cmd.ControlY2 != null

  // First control may be fully omitted when both are 0 (protobuf); second may still be set.
  return Boolean(hasFirstControl || hasSecondControl)
}

export function getPathCommands(decodedData: AnyRecord): PathCommand[] | null {
  const path = asRecord(decodedData.path) ?? asRecord(decodedData.Path)

  if (!path) return null

  const commands = asArray(path.commands) ?? asArray(path.Commands)

  if (!commands) return null

  const out: PathCommand[] = []

  for (const c of commands) {
    const cmd = asRecord(c)

    if (!cmd) continue

    const moveTo = asRecord(cmd.moveTo) ?? asRecord(cmd.move_to) ?? asRecord(cmd.MoveTo)

    if (moveTo) {
      // When decoding protobuf, zero values are often omitted (e.g. y: 0).
      // Treat missing coordinates as 0 (protobuf default) so we don't drop commands.
      const x = moveTo.x == null ? 0 : finiteNumber(moveTo.x)
      const y = moveTo.y == null ? 0 : finiteNumber(moveTo.y)

      if (x != null && y != null) out.push({ moveTo: { x, y } })
      continue
    }

    const lineTo = asRecord(cmd.lineTo) ?? asRecord(cmd.line_to) ?? asRecord(cmd.LineTo)

    if (lineTo) {
      // When decoding protobuf, zero values are often omitted (e.g. x: 0).
      // Treat missing coordinates as 0 (protobuf default) so we don't drop commands.
      const x = lineTo.x == null ? 0 : finiteNumber(lineTo.x)
      const y = lineTo.y == null ? 0 : finiteNumber(lineTo.y)

      if (x != null && y != null) out.push({ lineTo: { x, y } })
      continue
    }

    const curveTo =
      asRecord(cmd.curveTo) ??
      asRecord(cmd.curve_to) ??
      asRecord(cmd.CurveTo) ??
      (hasCurveToFields(cmd) ? cmd : null)

    if (curveTo) {
      const controlX = readProtoCoord(curveTo.controlX ?? curveTo.control_x ?? curveTo.ControlX)
      const controlY = readProtoCoord(curveTo.controlY ?? curveTo.control_y ?? curveTo.ControlY)
      const x = readProtoCoord(curveTo.x)
      const y = readProtoCoord(curveTo.y)
      const c2x = readProtoCoord(
        curveTo.deltaX ??
          curveTo.delta_x ??
          curveTo.DeltaX ??
          curveTo.controlX2 ??
          (curveTo as AnyRecord).control_x2 ??
          (curveTo as AnyRecord).ControlX2
      )
      const c2y = readProtoCoord(
        curveTo.deltaY ??
          curveTo.delta_y ??
          curveTo.DeltaY ??
          curveTo.controlY2 ??
          (curveTo as AnyRecord).control_y2 ??
          (curveTo as AnyRecord).ControlY2
      )

      const entry: {
        controlX: number
        controlY: number
        x: number
        y: number
        deltaX: number
        deltaY: number
      } = { controlX, controlY, x, y, deltaX: c2x, deltaY: c2y }

      out.push({ curveTo: entry })
    }
  }

  return out.length > 0 ? out : null
}

/**
 * Shifts wire-format path commands (center-relative, matching legacy `page-editor`'s own
 * convention — `ODPath.prototype.addSegmentToPXML`, odeditorsvg.js:15091, subtracts
 * `width/2`/`height/2` on save) back to this app's top-left-relative convention (0..width ×
 * 0..height, the same one `shapeToPathCommands`/`ShapeRenderer` use). Inverse of the shift
 * applied in `stowed-element-builder.ts`'s `convertPathCommands` on save.
 */
export function offsetPathCommands(commands: PathCommand[], dx: number, dy: number): PathCommand[] {
  return commands.map((cmd) => {
    if ('moveTo' in cmd) return { moveTo: { x: cmd.moveTo.x + dx, y: cmd.moveTo.y + dy } }
    if ('lineTo' in cmd) return { lineTo: { x: cmd.lineTo.x + dx, y: cmd.lineTo.y + dy } }
    if ('curveTo' in cmd) {
      const ct = cmd.curveTo

      return {
        curveTo: {
          controlX: ct.controlX + dx,
          controlY: ct.controlY + dy,
          x: ct.x + dx,
          y: ct.y + dy,
          ...(typeof ct.deltaX === 'number' ? { deltaX: ct.deltaX + dx } : {}),
          ...(typeof ct.deltaY === 'number' ? { deltaY: ct.deltaY + dy } : {}),
          ...(typeof ct.controlX2 === 'number' ? { controlX2: ct.controlX2 + dx } : {}),
          ...(typeof ct.controlY2 === 'number' ? { controlY2: ct.controlY2 + dy } : {}),
        },
      }
    }

    return cmd
  })
}
